import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  const handleGoogleLogin = () => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin)}`;
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 6) errors.push('minimálne 6 znakov');
    if (!/[A-Z]/.test(password)) errors.push('jedno veľké písmeno');
    if (!/[0-9]/.test(password)) errors.push('jedno číslo');
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(password)) errors.push('jeden špeciálny znak');
    return errors;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginForm)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Prihlásenie zlyhalo');
      }
      
      toast.success('Úspešne prihlásený!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate password
    const passwordErrors = validatePassword(registerForm.password);
    if (passwordErrors.length > 0) {
      toast.error(`Heslo musí obsahovať: ${passwordErrors.join(', ')}`);
      return;
    }
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Heslá sa nezhodujú');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: registerForm.name,
          email: registerForm.email,
          password: registerForm.password
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Registrácia zlyhala');
      }
      
      toast.success('Registrácia úspešná! Skontrolujte svoj email pre overenie účtu.');
      setMode('login');
      setLoginForm({ email: registerForm.email, password: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    const errors = validatePassword(password);
    const strength = 4 - errors.length;
    if (strength <= 1) return { color: 'bg-red-500', text: 'Slabé' };
    if (strength === 2) return { color: 'bg-orange-500', text: 'Stredné' };
    if (strength === 3) return { color: 'bg-yellow-500', text: 'Dobré' };
    return { color: 'bg-green-500', text: 'Silné' };
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-4">
      <Card className="max-w-md w-full p-8 space-y-6" data-testid="login-card">
        <div className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_a65584c5-22b3-4ad8-951b-58f1980c5cae/artifacts/hts8ypom_logo_green_top.jpg" 
            alt="EVMarket Logo" 
            className="h-20 w-20 rounded-full mx-auto mb-4 cursor-pointer"
            onClick={() => navigate('/')}
          />
          <h1 className="text-3xl font-bold text-primary mb-2" style={{fontFamily: 'Manrope, sans-serif'}}>EVMarket</h1>
          <p className="text-slate-600">
            {mode === 'login' ? 'Prihláste sa do svojho účtu' : 'Vytvorte si nový účet'}
          </p>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b">
          <button
            data-testid="login-tab"
            className={`flex-1 py-2 text-center font-medium transition-colors ${
              mode === 'login' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setMode('login')}
          >
            Prihlásenie
          </button>
          <button
            data-testid="register-tab"
            className={`flex-1 py-2 text-center font-medium transition-colors ${
              mode === 'register' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setMode('register')}
          >
            Registrácia
          </button>
        </div>

        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="login-email-input"
                  type="email"
                  placeholder="vas@email.sk"
                  className="pl-10"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Heslo</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="pl-10 pr-10"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <Button
              data-testid="login-submit-button"
              type="submit"
              className="w-full bg-primary text-white hover:bg-slate-800 font-semibold h-12"
              disabled={loading}
            >
              {loading ? 'Prihlasujem...' : 'Prihlásiť sa'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Meno</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="register-name-input"
                  type="text"
                  placeholder="Vaše meno"
                  className="pl-10"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="register-email-input"
                  type="email"
                  placeholder="vas@email.sk"
                  className="pl-10"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Heslo</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="register-password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="pl-10 pr-10"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {registerForm.password && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className={`h-1 flex-1 rounded ${getPasswordStrength(registerForm.password).color}`}></div>
                    <span className="text-xs text-slate-500">{getPasswordStrength(registerForm.password).text}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Min. 6 znakov, 1 veľké písmeno, 1 číslo, 1 špeciálny znak
                  </p>
                </div>
              )}
            </div>
            
            <div>
              <Label className="text-sm font-medium">Potvrdiť heslo</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="register-confirm-password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="pl-10"
                  value={registerForm.confirmPassword}
                  onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                  required
                />
              </div>
              {registerForm.confirmPassword && registerForm.password !== registerForm.confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Heslá sa nezhodujú</p>
              )}
            </div>
            
            <Button
              data-testid="register-submit-button"
              type="submit"
              className="w-full bg-primary text-white hover:bg-slate-800 font-semibold h-12"
              disabled={loading}
            >
              {loading ? 'Registrujem...' : 'Zaregistrovať sa'}
            </Button>
          </form>
        )}

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-slate-500">alebo</span>
          </div>
        </div>

        <Button
          data-testid="google-login-button"
          onClick={handleGoogleLogin}
          variant="outline"
          className="w-full font-semibold h-12"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Prihlásiť sa cez Google
        </Button>
      </Card>
    </div>
  );
}

export default LoginPage;
